// Shadcn-style Select Stimulus controller
// Provides dropdown select functionality with keyboard navigation
// Mimics the behavior of Shadcn/UI Select component

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['trigger', 'content', 'chevron', 'valueText', 'hiddenInput']
  static values = {
    value: String
  }

  connect() {
    this.isOpen = false
    this.selectedValue = this.valueValue || ''

    // Typeahead filtering state
    this.typeaheadBuffer = ''
    this.typeaheadTimeout = null

    // Close dropdown when clicking outside
    this.boundCloseOnOutsideClick = this.closeOnOutsideClick.bind(this)
    document.addEventListener('click', this.boundCloseOnOutsideClick)

    // Close dropdown on escape key
    this.boundCloseOnEscape = this.closeOnEscape.bind(this)
    document.addEventListener('keydown', this.boundCloseOnEscape)

    // Typeahead filtering on keypress
    this.boundHandleTypeahead = this.handleTypeahead.bind(this)
    document.addEventListener('keydown', this.boundHandleTypeahead)

    // Close when other dropdowns open
    this.boundCloseOnOtherOpen = this.closeOnOtherOpen.bind(this)
    document.addEventListener('shadcn-select:opened', this.boundCloseOnOtherOpen)
    document.addEventListener('floating-dropdown:open', this.boundCloseOnOtherOpen)
  }

  disconnect() {
    document.removeEventListener('click', this.boundCloseOnOutsideClick)
    document.removeEventListener('keydown', this.boundCloseOnEscape)
    document.removeEventListener('keydown', this.boundHandleTypeahead)
    document.removeEventListener('shadcn-select:opened', this.boundCloseOnOtherOpen)
    document.removeEventListener('floating-dropdown:open', this.boundCloseOnOtherOpen)

    if (this.typeaheadTimeout) {
      clearTimeout(this.typeaheadTimeout)
    }
  }

  // Toggle dropdown visibility
  toggle(event) {
    event.stopPropagation()

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  // Open dropdown
  open() {
    // Notify other dropdowns to close (both shadcn-select and floating-dropdown types)
    document.dispatchEvent(new CustomEvent('floating-dropdown:open', {
      detail: { controller: this, element: this.element }
    }))

    this.isOpen = true
    this.contentTarget.classList.remove('hidden')
    this.triggerTarget.setAttribute('aria-expanded', 'true')

    // Calculate and apply dropdown position before showing
    const shouldOpenUpward = this.calculateDropdownPosition()
    this.applyDropdownPosition(shouldOpenUpward)

    // Rotate chevron to indicate open state
    this.chevronTarget.classList.add('rotate-180')

    // Focus first option or selected option
    const selectedOption = this.contentTarget.querySelector('[data-shadcn-select-value-param="' + this.selectedValue + '"]')
    const firstOption = this.contentTarget.querySelector('[data-shadcn-select-value-param]')

    if (selectedOption) {
      selectedOption.focus()
    } else if (firstOption) {
      firstOption.focus()
    }

    this.dispatch('opened', {
      detail: {
        element: this.element
      }
    })
  }

  // Close dropdown
  close() {
    this.isOpen = false
    this.contentTarget.classList.add('hidden')
    this.triggerTarget.setAttribute('aria-expanded', 'false')
    this.chevronTarget.classList.remove('rotate-180')
    this.triggerTarget.focus()

    // Clear typeahead state and highlights
    this.typeaheadBuffer = ''
    this.clearTypeaheadHighlights()

    this.dispatch('closed')
  }

  // Select an option
  select(event) {
    const value = event.params.value
    const label = event.params.label || event.target.textContent.trim()

    // Update selected value
    this.selectedValue = value
    this.valueValue = value

    // Update UI
    this.updateSelectedOption(value)
    this.updateValueText(label)
    this.updateHiddenInput(value)

    // Close dropdown
    this.close()

    // Dispatch change event
    this.dispatch('change', {
      detail: {
        value: value,
        label: label
      }
    })

    // Trigger form submission or page navigation if needed
    this.handleValueChange(value)
  }

  // Update visual state of selected option
  updateSelectedOption(newValue) {
    // Remove selection from all options
    this.contentTarget.querySelectorAll('[data-shadcn-select-value-param]').forEach(option => {
      option.classList.remove('bg-orange-50', 'text-orange-700')
      option.classList.add('text-gray-900')

      // Remove check icon
      const checkIcon = option.querySelector('span:last-child')
      if (checkIcon && checkIcon.querySelector('svg')) {
        checkIcon.remove()
      }
    })

    // Add selection to new option
    const newSelectedOption = this.contentTarget.querySelector('[data-shadcn-select-value-param="' + newValue + '"]')
    if (newSelectedOption) {
      newSelectedOption.classList.add('bg-orange-50', 'text-orange-700')
      newSelectedOption.classList.remove('text-gray-900')

      // Add check icon
      const checkIcon = document.createElement('span')
      checkIcon.className = 'absolute inset-y-0 right-0 flex items-center pr-2'
      checkIcon.innerHTML = `
        <svg class="h-4 w-4 text-yellow-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
      `
      newSelectedOption.appendChild(checkIcon)
    }
  }

  // Update the displayed value text
  updateValueText(label) {
    this.valueTextTarget.textContent = label
  }

  // Update the hidden input value
  updateHiddenInput(value) {
    this.hiddenInputTarget.value = value
  }

  // Handle value changes (e.g., for pagination)
  handleValueChange(value) {
    const hiddenInput = this.hiddenInputTarget

    // If this is a pagination select, trigger form submission
    if (hiddenInput.name === 'per_page') {
      this.submitPageSizeChange(value)
    }
  }

  // Submit page size change for pagination
  submitPageSizeChange(newPageSize) {
    const url = new URL(window.location.href)
    url.searchParams.set('per_page', newPageSize)
    url.searchParams.delete('page') // Reset to first page

    // Use Turbo to navigate
    Turbo.visit(url.toString())
  }

  // Handle keyboard navigation
  handleKeydown(event) {
    if (!this.isOpen) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        this.open()
      }
      return
    }

    const options = Array.from(this.contentTarget.querySelectorAll('[data-shadcn-select-value-param]'))
    const currentIndex = options.indexOf(document.activeElement)

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0
        options[nextIndex].focus()
        break

      case 'ArrowUp':
        event.preventDefault()
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1
        options[prevIndex].focus()
        break

      case 'Enter':
      case ' ':
        event.preventDefault()
        if (document.activeElement.dataset.shadcnSelectValueParam) {
          this.select({
            params: { value: document.activeElement.dataset.shadcnSelectValueParam },
            target: document.activeElement
          })
        }
        break

      case 'Escape':
        this.close()
        break

      case 'Tab':
        this.close()
        break
    }
  }

  // Close dropdown when clicking outside
  closeOnOutsideClick(event) {
    if (this.isOpen && !this.element.contains(event.target)) {
      this.close()
    }
  }

  // Close dropdown on escape key
  closeOnEscape(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close()
    }
  }

  // Close this dropdown when another dropdown opens
  closeOnOtherOpen(event) {
    if (!this.isOpen) return

    // Check if event came from this controller (avoid closing self)
    if (event.detail) {
      // For shadcn-select:opened events
      if (event.detail.element === this.element) return
      // For floating-dropdown:open events
      if (event.detail.controller === this) return
    }

    this.close()
  }

  // Typeahead filtering: type "Sin" to find "Singapore"
  handleTypeahead(event) {
    if (!this.isOpen) return
    if (!this.contentTarget) return

    // CRITICAL: Only process typeahead if dropdown has focus
    // Prevents unintended activation when user interacts with other page elements
    const active = document.activeElement
    const isTriggerFocused = active === this.triggerTarget
    const isContentFocused = active === this.contentTarget
    const isOptionFocused = this.contentTarget.contains(active)
    if (!(isTriggerFocused || isContentFocused || isOptionFocused)) return

    // Ignore special keys
    if (event.key.length !== 1) return
    if (event.ctrlKey || event.metaKey || event.altKey) return

    event.preventDefault()

    // Add character to buffer
    this.typeaheadBuffer += event.key.toLowerCase()

    // Clear buffer after 1 second of inactivity
    if (this.typeaheadTimeout) {
      clearTimeout(this.typeaheadTimeout)
    }
    this.typeaheadTimeout = setTimeout(() => {
      this.typeaheadBuffer = ''
      this.clearTypeaheadHighlights()
    }, 1000)

    // Find matching option
    const options = Array.from(this.contentTarget.querySelectorAll('[data-shadcn-select-value-param]'))

    // Clear previous highlights
    this.clearTypeaheadHighlights()

    const matchedOption = options.find(option => {
      const label = option.textContent.trim().toLowerCase()
      return label.startsWith(this.typeaheadBuffer)
    })

    if (matchedOption) {
      matchedOption.focus()
      // Scroll into view if needed
      matchedOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' })

      // Highlight the matched portion
      this.highlightMatch(matchedOption, this.typeaheadBuffer)
    }
  }

  // Highlight matched text in the option
  // SECURITY: Uses safe DOM manipulation instead of innerHTML to prevent XSS
  highlightMatch(option, searchTerm) {
    const textNode = option.querySelector('span') || option
    const originalText = textNode.textContent.trim()
    const lowerText = originalText.toLowerCase()
    const matchIndex = lowerText.indexOf(searchTerm.toLowerCase())

    if (matchIndex === 0 && searchTerm.length > 0) {
      const before = originalText.substring(0, matchIndex)
      const match = originalText.substring(matchIndex, matchIndex + searchTerm.length)
      const after = originalText.substring(matchIndex + searchTerm.length)

      // Store original text for restoration
      if (!textNode.dataset.originalText) {
        textNode.dataset.originalText = originalText
      }

      // Clear existing content
      textNode.innerHTML = ''

      // Build DOM safely without innerHTML injection
      if (before) {
        textNode.appendChild(document.createTextNode(before))
      }

      // Create and append mark element
      const mark = document.createElement('mark')
      mark.className = 'bg-yellow-200 font-semibold text-gray-900'
      mark.textContent = match
      textNode.appendChild(mark)

      if (after) {
        textNode.appendChild(document.createTextNode(after))
      }
    }
  }

  // Clear all typeahead highlights
  clearTypeaheadHighlights() {
    if (!this.contentTarget) return

    const highlightedOptions = this.contentTarget.querySelectorAll('[data-original-text]')
    highlightedOptions.forEach(node => {
      if (node.dataset.originalText) {
        node.textContent = node.dataset.originalText
        delete node.dataset.originalText
      }
    })
  }

  // Calculate if dropdown should open upward based on available space
  calculateDropdownPosition() {
    const triggerRect = this.triggerTarget.getBoundingClientRect()
    const contentHeight = this.contentTarget.offsetHeight || 240 // max-h-60 = 240px
    const viewportHeight = window.innerHeight

    // Calculate available space below and above the trigger
    const spaceBelow = viewportHeight - triggerRect.bottom
    const spaceAbove = triggerRect.top

    // Open upward if there's not enough space below but more space above
    return spaceBelow < contentHeight && spaceAbove > spaceBelow
  }

  // Apply CSS classes for upward or downward positioning
  applyDropdownPosition(shouldOpenUpward) {
    if (shouldOpenUpward) {
      // Position dropdown above trigger
      this.contentTarget.classList.remove('mt-1')
      this.contentTarget.classList.add('bottom-full', 'mb-1')
    } else {
      // Position dropdown below trigger (default)
      this.contentTarget.classList.remove('bottom-full', 'mb-1')
      this.contentTarget.classList.add('mt-1')
    }
  }
}